import { useRef } from 'react'
import { intl } from '../../../../../../i18n'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import {
  BulkSheetFilter,
  FilterSelect,
  FilterInput,
  FilterFooter,
} from '../common'

export type NumberFilter = {
  operator: NumberFilterOperator
  value: number | undefined
}

export enum NumberFilterOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_OR_EQUALS = 'LESS_THAN_OR_EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_OR_EQUALS = 'GREATER_THAN_OR_EQUALS',
  BLANK = 'BLANK',
  NOT_BLANK = 'NOT_BLANK',
}

export const NumberFilterOperatorLabel = {
  [NumberFilterOperator.EQUALS]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.equals',
  }),
  [NumberFilterOperator.NOT_EQUALS]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.notEquals',
  }),
  [NumberFilterOperator.LESS_THAN]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.lessThan',
  }),
  [NumberFilterOperator.LESS_THAN_OR_EQUALS]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.lessThanOrEquals',
  }),
  [NumberFilterOperator.GREATER_THAN]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.greaterThan',
  }),
  [NumberFilterOperator.GREATER_THAN_OR_EQUALS]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.greaterThanOrEquals',
  }),
  [NumberFilterOperator.BLANK]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.blank',
  }),
  [NumberFilterOperator.NOT_BLANK]: intl.formatMessage({
    id: 'bulksheet.filter.numberFilter.operator.notBlank',
  }),
}

import _ from 'lodash'

const BLANK_OPERATORS = [
  NumberFilterOperator.BLANK,
  NumberFilterOperator.NOT_BLANK,
]

const buildNumberFilterModel = (
  operator: NumberFilterOperator,
  value: number | undefined
): NumberFilter | null => {
  const isBlankOp = BLANK_OPERATORS.includes(operator)
  const isActive = isBlankOp || value !== undefined
  return isActive
    ? {
        operator,
        value: isBlankOp
          ? undefined
          : Number.isFinite(value)
            ? value
            : undefined,
      }
    : null
}

const getNumberModelAsString = (
  operator: NumberFilterOperator,
  value: number | undefined
): string => {
  if (BLANK_OPERATORS.includes(operator)) {
    return `(${operator.charAt(0)}${_.lowerCase(operator.slice(1))})`
  }
  const symbolMap: Record<string, string> = {
    [NumberFilterOperator.EQUALS]: '=',
    [NumberFilterOperator.NOT_EQUALS]: '≠',
    [NumberFilterOperator.LESS_THAN]: '<',
    [NumberFilterOperator.LESS_THAN_OR_EQUALS]: '≤',
    [NumberFilterOperator.GREATER_THAN]: '>',
    [NumberFilterOperator.GREATER_THAN_OR_EQUALS]: '≥',
  }
  return `${symbolMap[operator] ?? ''} ${value}`
}

type Props = CustomFilterProps

export const ClientSideNumberFilter = ({
  model,
  onModelChange,
  getValue,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // Derive state from model (single source of truth)
  const operator =
    (model?.operator as NumberFilterOperator) ?? NumberFilterOperator.EQUALS
  const value = model?.value as number | undefined

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.isAdded) return true
      const numberValue = getValue(params.node)
      if (BLANK_OPERATORS.includes(operator)) {
        switch (operator) {
          case NumberFilterOperator.BLANK:
            return !isFinite(numberValue)
          case NumberFilterOperator.NOT_BLANK:
            return isFinite(numberValue)
        }
      }
      if (value === undefined || !isFinite(value)) return true
      switch (operator) {
        case NumberFilterOperator.EQUALS:
          return numberValue === value
        case NumberFilterOperator.NOT_EQUALS:
          return numberValue !== value
        case NumberFilterOperator.LESS_THAN:
          return numberValue < value
        case NumberFilterOperator.LESS_THAN_OR_EQUALS:
          return numberValue <= value
        case NumberFilterOperator.GREATER_THAN:
          return numberValue > value
        case NumberFilterOperator.GREATER_THAN_OR_EQUALS:
          return numberValue >= value
        default:
          // should never happen
          console.warn('invalid filter type ' + operator)
          return false
      }
    },

    afterGuiAttached() {
      inputRef.current?.focus()
    },

    getModelAsString() {
      if (!model) return ''
      return getNumberModelAsString(operator, value)
    },
  })

  return (
    <BulkSheetFilter>
      <FilterSelect
        variant="outlined"
        value={operator}
        onChange={e =>
          onModelChange(
            buildNumberFilterModel(
              e.target.value as NumberFilterOperator,
              value
            )
          )
        }
        sx={{ width: '100%' }}
      >
        {Object.values(NumberFilterOperator).map(v => (
          <option key={`number-filter-option-${v}`} value={v}>
            {NumberFilterOperatorLabel[v]}
          </option>
        ))}
      </FilterSelect>
      <FilterInput
        type="number"
        inputRef={inputRef}
        value={value ?? ''}
        onChange={e => {
          const num = parseFloat(e.target.value)
          onModelChange(
            buildNumberFilterModel(
              operator,
              Number.isFinite(num) ? num : undefined
            )
          )
        }}
        placeholder={'Filter...'}
        sx={{ height: 22 }}
      />
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
